import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { FiExternalLink, FiRefreshCw, FiX } from 'react-icons/fi';
import { useBlockchain } from '../../contexts/BlockchainContext';
import { useClientConfig } from '../../contexts/ClientConfigContext';

/* Above deploy / asset modals (typically 1100–1150) so the drawer stays readable when opened from a modal. */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: flex-end;
  z-index: 1250;
`;

const Drawer = styled.div`
  width: min(520px, 100%);
  height: 100%;
  background: linear-gradient(180deg, #15152e 0%, #0f0f23 100%);
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  padding: 1.5rem;
  overflow-y: auto;
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    font-size: 1.2rem;
    flex: 1;
    min-width: 0;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const IconButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #fff;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg.spinning {
    animation: ${spin} 0.75s linear infinite;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const CloseButton = styled(IconButton)``;

const Hash = styled.div`
  font-family: Consolas, Monaco, monospace;
  word-break: break-all;
  color: #4facfe;
  margin-bottom: 0.5rem;
`;

const ExplorerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 1rem;
  padding: 0.45rem 0.85rem;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #fff;
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.75rem;
  align-items: start;

  .label {
    color: #a0a0a0;
    font-size: 0.9rem;
  }

  .value {
    color: #fff;
    font-size: 0.95rem;
    word-break: break-all;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'confirmed' || status === 'success'
      ? 'rgba(0, 255, 136, 0.12)'
      : status === 'failed'
        ? 'rgba(255, 107, 107, 0.12)'
        : 'rgba(79, 172, 254, 0.12)'};
  color: ${({ status }) =>
    status === 'confirmed' || status === 'success'
      ? '#00ff88'
      : status === 'failed'
        ? '#ff6b6b'
        : '#4facfe'};
  border: 1px solid currentColor;
`;

const StateText = styled.div`
  color: #a0a0a0;
  line-height: 1.5;
`;

const normalizeStatus = (status) => {
  const normalized = String(status || '').toLowerCase().trim();
  if (normalized === '0x1' || normalized === '1') {
    return 'confirmed';
  }
  if (normalized === '0x0' || normalized === '0') {
    return 'failed';
  }
  if (
    normalized === 'success' ||
    normalized === 'confirmed' ||
    normalized === 'succeeded'
  ) {
    return 'confirmed';
  }
  if (normalized === 'failed' || normalized === 'failure' || normalized === 'reverted') {
    return 'failed';
  }
  return normalized || 'pending';
};

/** Parse RPC-style hex or decimal string to integer for display. */
const parseQuantityInt = (raw) => {
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }
  const s = String(raw).trim();
  if (/^0x[0-9a-fA-F]+$/i.test(s)) {
    return Number.parseInt(s, 16);
  }
  if (/^\d+$/.test(s)) {
    return Number.parseInt(s, 10);
  }
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

const BI = (v) => window.BigInt(v);

/** Parse value field (wei) as BigInt from hex or decimal string. */
const parseWeiBigInt = (raw) => {
  if (raw === undefined || raw === null || raw === '') {
    return BI(0);
  }
  const s = String(raw).trim();
  try {
    if (/^0x[0-9a-fA-F]+$/i.test(s)) {
      return BI(s);
    }
    if (/^\d+$/.test(s)) {
      return BI(s);
    }
  } catch {
    /* fall through */
  }
  return null;
};

/** Format wei (BigInt) as ETH with trimmed fractional zeros. */
const formatWeiAsEth = (wei) => {
  if (wei === null) {
    return null;
  }
  const zero = BI(0);
  if (wei === zero) {
    return '0 ETH';
  }
  const base = BI(10) ** BI(18);
  const negative = wei < zero;
  const w = negative ? -wei : wei;
  const whole = w / base;
  const frac = w % base;
  if (frac === zero) {
    return `${negative ? '-' : ''}${whole.toString()} ETH`;
  }
  let fracStr = frac.toString().padStart(18, '0').replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole.toString()}.${fracStr} ETH`;
};

const formatTransactionValueDisplay = (raw) => {
  const wei = parseWeiBigInt(raw);
  if (wei === null) {
    return raw !== undefined && raw !== null && raw !== '' ? String(raw) : '0 ETH';
  }
  return formatWeiAsEth(wei);
};

const formatNonceDisplay = (raw) => {
  const n = parseQuantityInt(raw);
  if (n === null) {
    return 'N/A';
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

/** User-facing label for receipt / API status values. */
const formatStatusLabel = (rawStatus) => {
  const key = normalizeStatus(rawStatus);
  switch (key) {
    case 'confirmed':
      return 'Success';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    default:
      if (!rawStatus) {
        return 'Pending';
      }
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
};

function TransactionLookupDrawer({ transactionHash, onClose, title = 'Transaction Details' }) {
  const { lookupTransaction } = useBlockchain();
  const { transactionExplorerUrl, explorerConfigured } = useClientConfig();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadTransaction = useCallback(
    async ({ silent = false } = {}) => {
      if (!transactionHash) {
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
        setError('');
      }

      try {
        const result = await lookupTransaction(transactionHash);
        setTransaction(result);
        setError('');
      } catch (lookupError) {
        setError(lookupError.message || 'Transaction lookup failed.');
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [lookupTransaction, transactionHash]
  );

  useEffect(() => {
    loadTransaction({ silent: false });
  }, [loadTransaction]);

  if (!transactionHash) {
    return null;
  }

  const statusKey = transaction ? normalizeStatus(transaction.status) : 'pending';
  const statusLabel = transaction ? formatStatusLabel(transaction.status) : '';

  const drawer = (
    <Overlay onClick={onClose}>
      <Drawer onClick={(event) => event.stopPropagation()}>
        <Header>
          <h3>{title}</h3>
          <HeaderActions>
            <IconButton
              type="button"
              onClick={() => loadTransaction({ silent: true })}
              disabled={loading || refreshing}
              title="Refresh from chain"
              aria-label="Refresh transaction details"
            >
              <FiRefreshCw className={refreshing ? 'spinning' : ''} />
            </IconButton>
            <CloseButton type="button" onClick={onClose} aria-label="Close">
              <FiX />
            </CloseButton>
          </HeaderActions>
        </Header>

        <Hash>{transactionHash}</Hash>
        <ExplorerButton
          type="button"
          disabled={!explorerConfigured}
          title={
            explorerConfigured ? 'View on Explorer' : 'Block explorer URL not loaded from API'
          }
          onClick={() => {
            const url = transactionExplorerUrl(transactionHash);
            if (url) window.open(url, '_blank', 'noopener,noreferrer');
          }}
        >
          <FiExternalLink /> View on Explorer
        </ExplorerButton>

        {loading && <StateText>Fetching transaction details from the backend...</StateText>}

        {!loading && error && <StateText>{error}</StateText>}

        {!loading && !error && transaction && (
          <>
            {refreshing && (
              <StateText style={{ marginBottom: '0.75rem' }}>Refreshing…</StateText>
            )}
            <Card>
              <Grid>
                <Row>
                  <div className="label">Status</div>
                  <div className="value">
                    <StatusBadge status={statusKey}>
                      {statusLabel}
                    </StatusBadge>
                  </div>
                </Row>
                <Row>
                  <div className="label">Block</div>
                  <div className="value">{transaction.blockNumber ?? 'Pending'}</div>
                </Row>
                <Row>
                  <div className="label">From</div>
                  <div className="value">{transaction.from || 'N/A'}</div>
                </Row>
                <Row>
                  <div className="label">To</div>
                  <div className="value">{transaction.to || 'Contract creation'}</div>
                </Row>
                <Row>
                  <div className="label">Value</div>
                  <div className="value">{formatTransactionValueDisplay(transaction.value)}</div>
                </Row>
                <Row>
                  <div className="label">Nonce</div>
                  <div className="value">{formatNonceDisplay(transaction.nonce)}</div>
                </Row>
                <Row>
                  <div className="label">Contract</div>
                  <div className="value">{transaction.contractAddress || 'N/A'}</div>
                </Row>
                {transaction.nftTokenIds?.length > 0 && (
                  <Row>
                    <div className="label">
                      {transaction.nftTokenIds.length === 1 ? 'Token ID' : 'Token IDs'}
                    </div>
                    <div className="value">{transaction.nftTokenIds.join(', ')}</div>
                  </Row>
                )}
              </Grid>
            </Card>

            <Card>
              <Row>
                <div className="label">Input</div>
                <div className="value">{transaction.input || 'N/A'}</div>
              </Row>
            </Card>
          </>
        )}
      </Drawer>
    </Overlay>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(drawer, document.body);
}

export default TransactionLookupDrawer;
